import { getUrqlFetchOptions, urqlClient } from '@app/services/urql-client';
import { call, select } from '@app/store/typed-saga-effects';
import { getBaseUrl } from '@hopin-team/dashboard-constants';
import { DocumentNode } from 'graphql';
import {
  CombinedError,
  OperationContext,
  OperationResult,
  TypedDocumentNode,
} from 'urql';

import { State } from './state';

export function* fetchAuthenticated<T>(
  url: string,
  method = 'GET',
  token: string,
  body?: any,
  throwErrorResponse = false,
  headers = {},
  returnType: 'json' | 'text' = 'json',
) {
  const res = yield* call(() =>
    fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(body),
    }),
  );
  if (res.status === 401) {
    yield* call(console.log, 'Unauthorized');
    return;
  }
  if (res.status === 204) {
    return null;
  }
  if (!res.ok) {
    if (throwErrorResponse) {
      const errors = yield* call(() => res.json());
      throw errors;
    }
    throw new Error(res.status.toString());
  }

  if (returnType === 'text') {
    return yield* call(() => res.text() as Promise<T>);
  }
  return yield* call(() => res.json() as Promise<T>);
}

export function* fetchContentApi<T>(
  route: string,
  method = 'GET',
  body?: any,
  headers?: Record<string, string>,
  returnType?: 'json' | 'text',
) {
  const auth = yield* select((state: State) => state.contentHub.authentication);

  if (auth.status !== 'success') return;

  return yield* call(() =>
    fetchAuthenticated<T>(
      `${auth.contentApiEndpoint}${route}`,
      method,
      auth.contentApiAuthToken,
      body,
      true,
      headers,
      returnType,
    ),
  );
}

type AuthenticationResult = {
  token: string;
};

type ScopedAuthenticationResult<T extends Record<string, string>> =
  AuthenticationResult & {
    endpoints: T;
  };

export function* authenticateScoped(scopePayload: Record<string, string>) {
  try {
    const response = yield* call(() =>
      fetch(`${getBaseUrl()}/users/scoped_sso`, {
        method: 'POST',
        body: JSON.stringify(scopePayload),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    );

    if (!response.ok) return null;

    return yield* call(
      () =>
        response.json() as Promise<
          ScopedAuthenticationResult<{ content_api: string }>
        >,
    );
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function* gqlQuery<TData, TVariables extends Record<string, any>>(
  query: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  context?: Partial<OperationContext>,
) {
  const authToken = yield* select((state: State) => state.user.authToken);

  if (!authToken)
    return {
      error: new CombinedError({
        networkError: new Error('Unauthorized'),
      }),
    } as OperationResult<TData, TVariables>;

  const result = yield* call(() =>
    urqlClient
      .query(query, variables, {
        fetchOptions: getUrqlFetchOptions(authToken),
        ...context,
      })
      .toPromise(),
  );

  return result;
}

export function* gqlMutation<TData, TVariables extends Record<string, any>>(
  mutation: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  context?: Partial<OperationContext>,
) {
  const authToken = yield* select((state: State) => state.user.authToken);

  if (!authToken)
    return {
      error: new CombinedError({
        networkError: new Error('Unauthorized'),
      }),
    } as OperationResult<TData, TVariables>;

  const result = yield* call(() =>
    urqlClient
      .mutation(mutation, variables, {
        fetchOptions: getUrqlFetchOptions(authToken),
        ...context,
      })
      .toPromise(),
  );
  return result;
}

export const uploadMedia = async (
  url: string,
  file: File,
): Promise<Response> => {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status}`);
  }

  return response;
};
